import { Transaction } from "@/types";
import { TableRow, TableCell } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { format } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { ArrowRight, Copy } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import CroppedId from "./CroppedId";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import formatCO2eCredits from "@/utils/formatAmountToCO2Credits";
import { Badge } from "./ui/badge";

interface TransactionItemProps {
  transaction: Transaction;
}

const TransactionItem = ({ transaction }: TransactionItemProps) => {
  const { t, language } = useLanguage();

  // Format date to match the design
  const date = new Date(transaction.ts);
  const formattedDate = format(date, "dd MMMM, yyyy");
  const formattedTime = format(date, "h:mm a");

  // Get from and to account details
  const fromAccount = transaction.fromAccount || {
    type: "unknown",
    title: "Unknown",
  };
  const toAccount = transaction.toAccount || {
    type: "unknown",
    title: "Unknown",
  };

  // Calculate retirement percentage
  const retiredAmount = transaction.retiredAmount || 0;
  const retirementPercentage =
    transaction.amount > 0
      ? Math.min(Math.round((retiredAmount / transaction.amount) * 100), 100)
      : 0;

  const co2eCredits = formatCO2eCredits(transaction.amount, t, language);

  // Helper function to capitalize first letter
  const capitalize = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Get localized text
  const retiredText = t("transaction.retired");

  // Copy to clipboard function
  const copyToClipboard = (text: string | undefined, label: string) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} ${t("common.copied")}`))
      .catch(() => toast.error(t("common.failed_copy")));
  };

  // Check if the toAccount is a customer type
  const isCustomerAccount = toAccount.type === "customer";

  return (
    <TableRow className="border-b hover:bg-gray-50">
      <TableCell className="py-4">
        <div className="flex flex-col">
          <div className="flex items-center">
            <span className="font-medium text-gray-800">
              {fromAccount.title}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 ml-1"
                    onClick={() =>
                      copyToClipboard(
                        // Only try to access id if it exists in fromAccount
                        "id" in fromAccount ? fromAccount.id : undefined,
                        t("transactions.project")
                      )
                    }
                  >
                    <Copy className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("transactionItem.tooltips.copyProjectId")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <span className="text-sm text-gray-500">{t("common.project")}</span>
        </div>
      </TableCell>

      <TableCell className="p-0 w-10 text-center">
        <ArrowRight className="text-gray-400 h-4 w-4" />
      </TableCell>

      <TableCell className="py-4">
        <div className="flex flex-col">
          <div className="flex items-center">
            <span className="font-medium text-gray-800">{toAccount.title}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 ml-1"
                    onClick={() =>
                      copyToClipboard(
                        // Only try to access id if it exists in toAccount
                        "id" in toAccount ? toAccount.id : undefined,
                        t("common.to")
                      )
                    }
                  >
                    <Copy className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("transactionItem.tooltips.copyRecipientId")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <span className="text-sm text-gray-500">
            {capitalize(toAccount.type)}
          </span>
        </div>
      </TableCell>

      <TableCell className="py-4">
        <div className="flex flex-col">
          <span className="text-gray-800">{formattedDate}</span>
          <span className="text-sm text-gray-500">{formattedTime}</span>
        </div>
      </TableCell>

      <TableCell className="py-4">
        <div className="flex items-center">
          <CroppedId
            id={transaction.id}
            size="md"
            showCopyButton={true}
            label={t("transactions.transactionId")}
          />
        </div>
        {transaction.batch.issuanceType && (
          <Badge variant="secondary" className="text-xs w-fit rounded-sm mt-1">
            {t(`common.issuanceType.${transaction.batch.issuanceType}`)}
          </Badge>
        )}
      </TableCell>

      <TableCell className="py-4">
        <div className="flex flex-col gap-1">
          {isCustomerAccount && (
            <div className="flex flex-col">
              <Progress
                value={retirementPercentage}
                className="h-2 w-24 bg-gray-200 [&>div]:bg-blue-500"
              />
              <span className="text-xs text-gray-600 mt-1">
                {retirementPercentage}% {retiredText}
              </span>
            </div>
          )}
          {!isCustomerAccount && <div className="text-xs text-gray-400">-</div>}
        </div>
      </TableCell>

      <TableCell className="py-4 text-right">
        <span
          className={cn(
            "font-semibold text-[16px]",
            transaction.type === "income" ? "text-blue-600" : "text-gray-800"
          )}
        >
          {co2eCredits}
        </span>
      </TableCell>
    </TableRow>
  );
};

export default TransactionItem;
